import { cn } from "@/lib/utils";
import { noop } from "lodash";
import { Check, type LucideProps } from "lucide-react";
import { useQueryState } from "nuqs";
import {
  type ForwardRefExoticComponent,
  Fragment,
  type RefAttributes,
  createContext,
  useContext,
  useMemo,
} from "react";

interface StepProps {
  title: string;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  description?: string;
  isCompleted?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

const Step: React.FC<StepProps> = ({
  title,
  description,
  isCompleted,
  isActive,
  onClick,
  ...props
}) => {
  return (
    <button
      type="button"
      className="flex items-center group"
      onClick={onClick}
      data-step
    >
      <div className="relative flex items-center justify-center">
        <div
          className={cn(
            "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ease-in-out transform group-hover:scale-110",
            isCompleted
              ? "text-success border-success bg-green-100"
              : isActive
                ? "border-primary scale-110 shadow-md"
                : "border-muted",
          )}
        >
          {isCompleted ? (
            <Check className="w-4 h-4" />
          ) : (
            props.icon && <props.icon className={cn("w-4 h-4")} />
          )}
        </div>
      </div>
      <div className="ml-4">
        <p
          className={cn(
            "text-sm font-medium transition-colors duration-300",
            isActive || isCompleted
              ? "text-foreground"
              : "text-muted-foreground",
          )}
        >
          {title}
        </p>
        {description && (
          <p
            className={cn(
              "text-sm text-muted-foreground transition-opacity duration-300",
              isActive && "opacity-100",
              !isActive && "opacity-75",
            )}
          >
            {description}
          </p>
        )}
      </div>
    </button>
  );
};

type Step = {
  title: string;
  description?: string;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
};

interface StepperProps {
  steps: Array<Step>;
  currentStep: number;
  onStepChange?: (step: number) => void;
}

export function Stepper({
  steps,
  currentStep,
  onStepChange = noop,
}: StepperProps) {
  return (
    <div className="w-full mx-auto flex flex-col items-center">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-8">
        {steps.map((step, index) => (
          <Fragment key={step.title}>
            <Step
              title={step.title}
              description={step.description}
              icon={step.icon}
              isCompleted={index < currentStep}
              isActive={index === currentStep}
              onClick={() => onStepChange(index)}
            />
            {index < steps.length - 1 && (
              <div className="hidden md:flex items-center relative w-24 h-[2px]">
                <div className="absolute w-full h-full bg-muted" />
                <div
                  className={cn(
                    "absolute h-full bg-primary transition-all duration-500 ease-in-out",
                    index < currentStep ? "w-full" : "w-0",
                  )}
                />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export const StepperContext = createContext<{
  currentStep: number;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  totalSteps: number;
}>({
  currentStep: 0,
  nextStep: () => {},
  prevStep: () => {},
  goToStep: () => {},
  totalSteps: 0,
});

export const useStepper = () => {
  const context = useContext(StepperContext);

  if (context === undefined) {
    throw new Error("useStepper must be used within a StepperContext Provider");
  }

  return context;
};

export const StepperProvider = ({
  children,
}: { children: React.ReactNode }) => {
  const [currentStep, setCurrentStep] = useQueryState("step", {
    defaultValue: 0,
    parse: (value) => Number.parseInt(value, 10),
    serialize: (value) => value.toString(),
  });

  const stepperValue = useMemo(() => {
    return {
      currentStep,
      get totalSteps() {
        return document.querySelectorAll("[data-step]").length;
      },
      nextStep: () => {
        const total = document.querySelectorAll("[data-step]").length;
        if (currentStep < total - 1) {
          setCurrentStep(currentStep + 1);
        }
      },
      prevStep: () => {
        if (currentStep > 0) {
          setCurrentStep(currentStep - 1);
        }
      },
      goToStep: (step: number) => {
        const total = document.querySelectorAll("[data-step]").length;
        if (step >= 0 && step < total) {
          setCurrentStep(step);
        }
      },
    };
  }, [currentStep, setCurrentStep]);

  return (
    <StepperContext.Provider value={stepperValue}>
      {children}
    </StepperContext.Provider>
  );
};
